/**
 * Pure data prep for the station charts. Does in the browser what the
 * reference app's server did (see spec §6): normalise Flumen rows, bucket,
 * insert outage gaps, build wind-rose cells, resolve safety-sector colours.
 */
import { parseStationTime } from './time';

// m/s thresholds shared by the speed AND gust lines: caution above 5, danger above 7.
// 7 mirrors flightSafety.js (mean OR gust > 7 → unsafe); 5–7 is demanding-launch territory.
export const WIND_ZONES = [5, 7];
// Shaded "ideal takeoff" band on the wind axis (m/s). Lower edge = where a forward launch gets real help.
export const SAFE_BAND = [2, 5];
// Below this the air is calm (rose bin 1); between CALM_MAX and SAFE_BAND[0] it is light (rose bin 2).
export const CALM_MAX = 0.5;

/**
 * Wind-axis tick positions: a fixed 2 m/s step on normal days so one gridline always means the same
 * thing, stepping up to 5 and then 10 m/s only when the axis would otherwise need more than ~8 lines
 * (storms). `axisMax` is the axis max after padding; the last position is the smallest multiple ≥ it.
 */
export function windTickStep(axisMax) {
    return axisMax > 40 ? 10 : axisMax > 16 ? 5 : 2;
}
export function windTickPositions(axisMax) {
    const step = windTickStep(axisMax || 0);
    const top = Math.max(step, Math.ceil((axisMax || 0) / step) * step);
    const positions = [];
    for (let v = 0; v <= top; v += step) positions.push(v);
    return positions;
}
export const MIN_RANGE_PRESSURE_HPA = 8;
export const MIN_RANGE_CLOUDBASE_M = 200;
export const ROSE_WINDOW_MIN = 60;

export const SECTOR_LABELS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];

// Rose speed bins: the two light bins (<0.5 calm, 0.5–2 light) as before, then the flight limits
// (SAFE_BAND 2–5 = ideal, WIND_ZONES 5 / 7) so the rose tells the same story as the timeline's
// zones and band. Colours: --chart-rose-1..6 (index = bin).
const STORM = 10;
export const SPEED_BINS = [
    { label: `<${CALM_MAX}`, min: 0, max: CALM_MAX },                                   // calm
    { label: `${CALM_MAX}-${SAFE_BAND[0]}`, min: CALM_MAX, max: SAFE_BAND[0] },         // light, too little
    { label: `${SAFE_BAND[0]}-${SAFE_BAND[1]}`, min: SAFE_BAND[0], max: SAFE_BAND[1] }, // ideal
    { label: `${WIND_ZONES[0]}-${WIND_ZONES[1]}`, min: WIND_ZONES[0], max: WIND_ZONES[1] }, // demanding
    { label: `${WIND_ZONES[1]}-${STORM}`, min: WIND_ZONES[1], max: STORM },             // unsafe
    { label: `>${STORM}`, min: STORM, max: Infinity },                                  // dangerous
];

const FIELDS = ['windSpeed', 'windGust', 'windDirection', 'temp', 'dewPoint', 'cloudBase', 'pressure', 'humidity'];
const MEAN_FIELDS = ['windSpeed', 'temp', 'dewPoint', 'cloudBase', 'pressure', 'humidity'];

const num = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
};

/** N centred on 0°: sector = round(deg / 22.5) mod 16. */
export function cardinalFor(deg) {
    const d = ((deg % 360) + 360) % 360;
    return SECTOR_LABELS[Math.round(d / 22.5) % 16];
}

export function speedBinFor(speed) {
    if (speed === null || speed === undefined || Number.isNaN(speed)) return null;
    const bin = SPEED_BINS.find((b) => speed >= b.min && speed < b.max);
    return bin ? bin.label : null;
}

export function normalizeRows(rows) {
    const out = [];
    for (const r of rows || []) {
        const t = parseStationTime(r.SEND_TIME);
        if (Number.isNaN(t)) continue;
        out.push({
            t,
            windSpeed: num(r.WIND_SP),
            windGust: num(r.WIND_GUST) ?? num(r.WIND_MAX),
            windDirection: num(r.WIND_ANG),
            temp: num(r.TEMP),
            dewPoint: num(r.D_POINT),
            cloudBase: num(r.CL_BASE),
            pressure: num(r.PRESSURE),
            humidity: num(r.MOIST),
        });
    }
    out.sort((a, b) => a.t - b.t);
    return out;
}

const HOUR = 3600e3;
export function resolutionFor(spanMs) {
    if (spanMs <= 6 * HOUR) return 0;
    if (spanMs <= 24 * HOUR) return 5 * 60e3;
    return 15 * 60e3;
}

const mean = (vals) => (vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null);

function vectorMeanDeg(degs) {
    if (!degs.length) return null;
    let sx = 0; let sy = 0;
    for (const d of degs) { const r = (d * Math.PI) / 180; sx += Math.cos(r); sy += Math.sin(r); }
    const deg = (Math.atan2(sy / degs.length, sx / degs.length) * 180) / Math.PI;
    const rounded = Math.round((((deg % 360) + 360) % 360) * 1e6) / 1e6; // kill -0 / fp noise
    return rounded === 360 ? 0 : rounded;
}

export function bucketPoints(points, bucketMs) {
    if (!bucketMs) return points;
    const groups = new Map();
    for (const p of points) {
        const key = Math.floor(p.t / bucketMs) * bucketMs;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(p);
    }
    const out = [];
    for (const [t, grp] of groups) {
        const b = { t };
        for (const f of MEAN_FIELDS) b[f] = mean(grp.map((p) => p[f]).filter((v) => v !== null));
        const gusts = grp.map((p) => p.windGust).filter((v) => v !== null);
        b.windGust = gusts.length ? Math.max(...gusts) : null;
        b.windDirection = vectorMeanDeg(grp.map((p) => p.windDirection).filter((v) => v !== null));
        out.push(b);
    }
    out.sort((a, b) => a.t - b.t);
    return out;
}

export function estimateSpacingMs(points) {
    if (points.length < 2) return Infinity;
    const diffs = [];
    for (let i = 1; i < points.length; i++) diffs.push(points[i].t - points[i - 1].t);
    diffs.sort((a, b) => a - b);
    return diffs[Math.floor(diffs.length / 2)];
}

const nullPoint = (t) => {
    const p = { t };
    for (const f of FIELDS) p[f] = null;
    return p;
};

export function insertGaps(points, thresholdMs) {
    if (points.length < 2) return points.slice();
    const out = [];
    for (let i = 0; i < points.length; i++) {
        if (i > 0 && points[i].t - points[i - 1].t > thresholdMs) out.push(nullPoint(points[i - 1].t + 1));
        out.push(points[i]);
    }
    return out;
}

export function prepareTimeline(points, spanMs) {
    const bucketMs = resolutionFor(spanMs);
    const b = bucketPoints(points, bucketMs);
    const threshold = 2 * (bucketMs || estimateSpacingMs(b));
    return insertGaps(b, threshold);
}

export function roseCells(points) {
    const counts = new Map();
    let total = 0;
    for (const p of points) {
        const bin = speedBinFor(p.windSpeed);
        if (bin === null || p.windDirection === null || p.windDirection === undefined) continue;
        total += 1;
        const key = `${cardinalFor(p.windDirection)}|${bin}`;
        counts.set(key, (counts.get(key) || 0) + 1);
    }
    const cells = [];
    for (const direction of SECTOR_LABELS) {
        for (const b of SPEED_BINS) {
            const count = counts.get(`${direction}|${b.label}`) || 0;
            cells.push({ direction, bin: b.label, count, percentage: total ? (count / total) * 100 : 0 });
        }
    }
    return { cells, total };
}

export { parseSectors } from '../sectors';

/**
 * Only explicitly-cleared sectors are flyable: unclassified → danger.
 * A station with no sector data at all → neutral (genuine "no information").
 */
export function sectorColor(deg, sectors) {
    if (!sectors) return 'var(--chart-neutral)';
    const c = cardinalFor(deg);
    if (sectors.green.includes(c)) return 'var(--chart-safe)';
    if (sectors.yellow.includes(c)) return 'var(--chart-caution)';
    return 'var(--chart-danger)';
}

/** Top-n directions by summed share, for the compact "Wind now" strip. */
export function dominantSectors(cells, n = 2) {
    const byDir = new Map();
    for (const c of cells || []) byDir.set(c.direction, (byDir.get(c.direction) || 0) + c.percentage);
    return [...byDir.entries()]
        .filter(([, pct]) => pct > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, n)
        .map(([direction, percentage]) => ({ direction, percentage }));
}

/** Speed bins with any share, top-n by share, returned in bin order with their palette index. */
/** Top-n speed bins by share of the rose window, largest first, with their palette index and percentage. */
export function presentBins(cells, n = 3) {
    const byBin = new Map();
    for (const c of cells || []) byBin.set(c.bin, (byBin.get(c.bin) || 0) + c.percentage);
    return SPEED_BINS
        .map((b, index) => ({ bin: b.label, index, percentage: byBin.get(b.label) || 0 }))
        .filter((b) => b.percentage > 0)
        .sort((a, b) => b.percentage - a.percentage)
        .slice(0, n);
}
